import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { downloadDocumentPdf } from '../api/hooks';
import type { DocumentSummary } from '../api/types';
import { useT } from '../i18n';
import { isOpen, TYPES } from '../lib/doc';
import { dt, dts, money } from '../lib/format';
import { useUi } from '../state/ui';
import { DueBadge } from './DueBadge';
import { SwipeRow } from './SwipeRow';
import { Tag } from './ui';

/**
 * Tabulka dokladů: jeden markup, dvě rozvržení v CSS (layout.css).
 * Mobil: dvouřádkový řádek jako v prototypu. Desktop: sloupce s hlavičkou.
 */
export function DocTable({
  children,
  className = '',
  selectable = false,
  partyLabel = 'Odběratel',
}: {
  children: ReactNode;
  className?: string;
  /** u přijatých dokladů je partner dodavatel */
  partyLabel?: string;
  /** řádky mají vlevo zaškrtávátko – hlavička se odsadí, aby sloupce lícovaly */
  selectable?: boolean;
}) {
  const cls = ['surface', 'doc-tbl', selectable ? 'selectable' : '', className];
  return (
    <section className={cls.filter(Boolean).join(' ')}>
      <div className="th">
        <span>Číslo</span>
        <span>{partyLabel}</span>
        <span>Typ</span>
        <span>Vystaveno</span>
        <span>Splatnost</span>
        <span>Částka</span>
        <span>Stav</span>
      </div>
      {children}
    </section>
  );
}

/**
 * Vykřičník u vydané faktury, ke které chybí nákupní doklad (přijatá faktura dodavatele,
 * výkupní list, kus ze skladu). Důvod je v titulku a pro čtečky obrazovky.
 */
export function PurchaseMark({ doc }: { doc: Pick<DocumentSummary, 'purchaseDoc'> }) {
  const p = doc.purchaseDoc;
  if (p?.state !== 'missing') return null;
  const text = `Chybí nákupní doklad: ${p.reasons.join('; ')}`;
  return (
    <span className="purchase-miss" title={text} data-purchase-missing>
      <span aria-hidden="true">!</span>
      <span className="sr-only">{text}</span>
    </span>
  );
}

/**
 * Řádek dokladu. Swipe doleva (nebo hover) odhalí rychlé akce: Úhrada u otevřených dokladů,
 * PDF u vystavených. `onPay` dodá stránka (otevře PaymentSheet); bez něj se akce nenabízí.
 */
export function DocRow({
  doc,
  onPay,
  child = false,
}: {
  doc: DocumentSummary;
  onPay?: (doc: DocumentSummary) => void;
  /** vnořený řádek pod dokladem (dobropis k faktuře, daňový doklad k záloze) */
  child?: boolean;
}) {
  const t = useT();
  const navigate = useNavigate();
  const { toast } = useUi();
  const open = isOpen(doc.status) && doc.type !== 'DB';
  const actions: { label: string; onClick: () => void; kind?: 'primary' | 'danger' }[] = [];
  if (open && onPay && doc.status !== 'draft') {
    actions.push({ label: t('row.pay'), kind: 'primary', onClick: () => onPay(doc) });
  }
  if (doc.number && doc.type !== 'DL') {
    actions.push({
      label: 'PDF',
      onClick: () => {
        downloadDocumentPdf(doc.id, doc.number ?? 'doklad').catch((e: Error) => toast(e.message));
      },
    });
  }
  const amount =
    doc.type === 'DL'
      ? `${doc.quantityTotal ?? '0'} ks`
      : `${doc.type === 'DB' ? '−' : ''}${money(doc.totalAmount, doc.currency ?? 'CZK')}`;
  return (
    <SwipeRow actions={actions}>
      <button
        type="button"
        className={`tr doc-row${child ? ' doc-child' : ''}`}
        onClick={() => navigate(`/doklady/${doc.id}`)}
      >
        <span className="c-num">
          {child && (
            <span className="child-mark" aria-hidden="true">
              ↳
            </span>
          )}
          {doc.number ?? 'Koncept'}
        </span>
        <span className="c-name">{doc.customerName}</span>
        <span className="c-type">
          <Tag>{TYPES[doc.type].label}</Tag>
        </span>
        <span className="c-issued">{dt(doc.issuedAt)}</span>
        <span className="c-due">
          {open ? (
            <>
              <span className="only-m">do {dts(doc.dueAt)}</span>
              <span className="only-d">{dt(doc.dueAt)}</span>
            </>
          ) : (
            <span className="only-d">{doc.type === 'DL' ? '—' : dt(doc.dueAt)}</span>
          )}
        </span>
        <span className="c-amt">{amount}</span>
        <span className="c-status">
          <DueBadge doc={doc} />
          <PurchaseMark doc={doc} />
        </span>
      </button>
    </SwipeRow>
  );
}
